import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { motion } from 'framer-motion';
import { Loader2, Mail, Phone, Globe, ArrowRight, ArrowLeft, Building2, Smartphone } from 'lucide-react';
import { PhoneInputField } from '@/components/ui/phone-input';
import styles from '../../../styles/PhoneInput.module.css';

interface ContactStepProps {
  formData: BusinessRegistrationForm;
  setFormData: (data: BusinessRegistrationForm) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
  errors?: { [key: string]: string };
}

export function ContactStep({ 
  formData, 
  setFormData, 
  onNext, 
  onBack,
  isLoading = false,
  errors = {}
}: ContactStepProps) {
  const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});

  const handleContactDataChange = (field: string, value: string) => {
    setLocalErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
    setFormData({
      ...formData,
      businessData: {
        ...formData.businessData,
        contact: {
          ...formData.businessData.contact,
          [field]: value
        }
      }
    });
  };

  const allErrors = { ...errors, ...localErrors };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, _fieldName: string, nextFieldId?: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextFieldId) {
        const nextField = document.getElementById(nextFieldId);
        nextField?.focus();
      } else {
        // If no next field, submit form
        onNext();
      }
    }
  };

  // Handle phone input change with separated values
  const handlePhoneInputChange = (phone: string, countryCode: string) => {
    setFormData({
      ...formData,
      businessData: {
        ...formData.businessData,
        contact: {
          ...formData.businessData.contact,
          phone,
          countryCode
        }
      }
    });
  };

  // Render helper for error messages
  const renderError = (field: string) => {
    const error = allErrors[field];
    return error ? (
      <p className="text-sm text-red-500 mt-1">{error}</p>
    ) : null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="space-y-6"
    >
      {/* Header Section with Clear Purpose */}
      <div className="text-center space-y-3 pb-6 border-b border-gray-200">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-[#97BC62] rounded-full flex items-center justify-center">
          <Phone className="h-8 w-8 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Business Contact Information</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Provide contact details for your business. This is how customers will reach you for bookings and inquiries.
          </p>
        </div>
      </div>

      {/* Business Contact Information */}
      <div className="space-y-6">
        {/* Business Email */}
        <div className="space-y-2">
          <Label htmlFor="businessEmail" className="flex items-center gap-1.5 text-sm font-medium">
            <Mail className="h-4 w-4 text-primary" />
            Business Email
          </Label>
          <Input
            id="businessEmail"
            type="email"
            value={formData.businessData.contact.email}
            onChange={(e) => handleContactDataChange('email', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'businessEmail', 'phone')}
            className={`${formData.businessData.contact.email ? 'border-green-500 bg-green-50/30' : ''} ${allErrors.businessEmail ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
            placeholder="business@example.com"
            disabled={isLoading}
            aria-invalid={!!allErrors.businessEmail}
          />
          {renderError('businessEmail')}
          <p className="text-xs text-gray-500">
            Customers will use this email to contact your business directly
          </p>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="flex items-center gap-1.5 text-sm font-medium">
            <Smartphone className="h-4 w-4 text-primary" />
            Business Phone Number
          </Label>
          
          <PhoneInputField
            value={formData.businessData.contact.phone}
            countryCode={formData.businessData.contact.countryCode || '+1'}
            onChange={handlePhoneInputChange}
            error={allErrors.phone}
            disabled={isLoading}
            nextFieldId="website"
            id="phone"
            name="phone"
          />
          
          <div className={styles.helperText}>
            <Smartphone className="h-3 w-3" />
            Select your country code and enter your business phone number
          </div>
          
          {renderError('phone')}
        </div>

        {/* Website (Optional) */}
        <div className="space-y-2">
          <Label htmlFor="website" className="flex items-center gap-1.5 text-sm font-medium">
            <Globe className="h-4 w-4 text-primary" />
            Website <span className="text-gray-400 font-normal">(Optional)</span>
          </Label>
          <Input
            id="website"
            type="url"
            value={formData.businessData.contact.website || ''}
            onChange={(e) => handleContactDataChange('website', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'website', 'bookingMode')}
            className={`${allErrors.website ? 'border-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary/20'}`}
            placeholder="https://www.yourwildlifebusiness.com"
            disabled={isLoading}
            aria-invalid={!!allErrors.website}
          />
          {renderError('website')}
          <p className="text-xs text-gray-500">
            Your business website URL (if you have one)
          </p>
        </div>

        {/* Booking Mode */}
        <div className="space-y-2">
          <Label htmlFor="bookingMode" className="flex items-center gap-1.5 text-sm font-medium">
            <Building2 className="h-4 w-4 text-primary" />
            How would you like to handle bookings?
          </Label>
          <Select
            value={formData.businessData.contact.bookingMode}
            onValueChange={(value: 'platform' | 'redirect') => handleContactDataChange('bookingMode', value)}
            disabled={isLoading}
          >
            <SelectTrigger className="border-gray-300 focus:border-primary focus:ring-primary/20">
              <SelectValue placeholder="Select booking method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="platform">
                <div className="space-y-1">
                  <div className="font-medium">Through Wildy Platform</div>
                  <div className="text-xs text-gray-500">Let customers book directly through our platform</div>
                </div>
              </SelectItem>
              <SelectItem value="redirect">
                <div className="space-y-1">
                  <div className="font-medium">Redirect to My Website</div>
                  <div className="text-xs text-gray-500">Send customers to your own booking system</div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            Choose how you want to manage customer bookings and payments
          </p>
        </div>

        {/* Booking URL - Only show if redirect mode is selected */}
        {formData.businessData.contact.bookingMode === 'redirect' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            <Label htmlFor="bookingUrl" className="flex items-center gap-1.5 text-sm font-medium">
              <Globe className="h-4 w-4 text-primary" />
              Booking Website URL
            </Label>
            <Input
              id="bookingUrl"
              type="url"
              value={formData.businessData.contact.bookingUrl || ''}
              onChange={(e) => handleContactDataChange('bookingUrl', e.target.value)}
              className="border-gray-300 focus:border-primary focus:ring-primary/20"
              placeholder="https://booking.yourwildlifebusiness.com"
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500">
              The URL where customers will be sent to make bookings
            </p>
          </motion.div>
        )}

        {/* WhatsApp Number (Optional) */}
        <div className="space-y-2">
          <Label htmlFor="whatsappNumber" className="flex items-center gap-1.5 text-sm font-medium">
            <Smartphone className="h-4 w-4 text-primary" />
            WhatsApp Number <span className="text-gray-400 font-normal">(Optional)</span>
          </Label>
          <Input
            id="whatsappNumber"
            type="tel"
            value={formData.businessData.contact.whatsappNumber || ''}
            onChange={(e) => handleContactDataChange('whatsappNumber', e.target.value)}
            className="border-gray-300 focus:border-primary focus:ring-primary/20"
            placeholder="+1234567890"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500">
            Include country code (e.g., +1234567890) for WhatsApp contact
          </p>
        </div>
      </div>

      {/* Contact Method Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Phone className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">Customer Contact</p>
            <p className="text-blue-800">
              These contact details will be displayed on your business profile. Customers can use them to reach you for bookings, inquiries, and support.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button 
          variant="outline" 
          onClick={onBack}
          disabled={isLoading}
          className="transition-all duration-200 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button 
          onClick={onNext}
          disabled={isLoading}
          className="min-w-[120px] bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 transition-all duration-200"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Validating...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              Continue
              <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </Button>
      </div>
    </motion.div>
  );
}